const findOption = function(node, value)
{
	return Array.from(node?.list?.options ?? []).find(option => option.value == value);
};

const normalizeForMatch = function(str)
{
	return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
};

/** Só dígitos — para casar CNPJ/CPF digitado com rótulo que traz documento mascarado. */
export const digitsOnly = function(str)
{
	return String(str || '').replace(/\D/g, '');
};

/** Quando o usuário digita 11 ou 14 dígitos (com ou sem máscara), encontra a option cujo rótulo contém esse documento. */
export const findOptionByDocumentDigits = function(options, rawTyped)
{
	const d = digitsOnly(String(rawTyped || '').trim());
	if (d.length !== 11 && d.length !== 14)
		return undefined;
	return Array.from(options ?? []).find(o => digitsOnly(o.innerText || '').includes(d));
};

const optionMatchesTyped = function(option, typed)
{
	if (!typed || !option?.innerText) return false;
	const nTyped = normalizeForMatch(typed);
	const nOption = normalizeForMatch(option.innerText);
	return nOption === nTyped || nOption.startsWith(nTyped) || nOption.includes(nTyped);
};

const isValidId = function(val)
{
	if (val == null || val === '' || val === -1 || val === '-1') return false;
	const n = Number(val);
	return !isNaN(n) && n >= 0;
};

/** Valor composto `warehouse_id:location_id` (saídas multi-armazém — obs_load_options `compositeWhLoc`). */
const isCompositeWhLocValue = function(val)
{
	return /^\d+:\d+$/.test(String(val ?? '').trim());
};

/** Vários <option> podem ter o mesmo rótulo (ex.: mesmo lot_number em entradas distintas): manter o id já selecionado quando possível. */
const disambiguateByPreferred = function(candidates, preferredValue)
{
	if (!candidates?.length)
		return undefined;
	if (candidates.length === 1)
		return candidates[0];
	if (isValidId(preferredValue) || isCompositeWhLocValue(preferredValue))
	{
		const pref = candidates.find(o => String(o.value) === String(preferredValue));
		if (pref)
			return pref;
	}
	return candidates[0];
};

/** No blur: priorizar id (value) e rótulo exato; só depois busca parcial — evita "533" cair no 1º CNPJ/rótulo que contenha a substring. */
const resolveOptionOnBlur = function(options, rawTyped, preferredValue)
{
	const typed = String(rawTyped || '').trim();
	if (!typed.length)
		return undefined;
	const byValue = options.find(o => String(o.value) === typed || o.value == typed);
	if (byValue)
		return byValue;
	const labelMatches = options.filter(o => o.innerText && o.innerText.trim() === typed);
	const byLabelExact = disambiguateByPreferred(labelMatches, preferredValue);
	if (byLabelExact)
		return byLabelExact;
	const byDoc = findOptionByDocumentDigits(options, typed);
	if (byDoc)
		return byDoc;
	return options.find(o => optionMatchesTyped(o, typed));
};

const setOption = function(node, callback)
{
	let options = Array.from(node?.list?.options ?? []);
	let option = options.find(callback);
	// -2: invalid, -1: empty. Opcional + sem match: se lista vazia (ex: produto sem estoque), manter texto digitado (-2); senão tratar como vazio (-1)
	let new_value = option?.value ?? (node.value.length ? (this.isRequired() ? -2 : (options.length === 0 ? -2 : -1)) : -1);
	let old_value = this.getValue() ?? -1;
	// Input vazio e campo tem id: só preservar se opções ainda não carregaram (não há option para old_value). Se há option, usuário pode ter limpado de propósito.
	if (new_value === -1 && !node.value.length && (isValidId(old_value) || isCompositeWhLocValue(old_value)) && !findOption(node, old_value))
		return void this.refresh();
	if (new_value != old_value)
	{
		this.setValue(new_value);
		this.checkErrorState();
	}
	this.refresh();		// Must be out here or it won't show properly when you clear the field and retype the old value
};

export default function (...args)
{
	return {

		events: ['input', 'blur'],

		checkValue(value = this.getValue())
		{
			let node = document.querySelector(this.selector);
			if (findOption(node, value))
				return true;
			// Campo opcional com id numérico válido: aceitar mesmo sem opção (ex.: lista ainda não carregou)
			if (!this.isRequired() && isValidId(value))
				return true;
			// `warehouse_id:location_id` (datalist de posições na saída): válido antes da lista IndexedDB terminar de popular
			if (isCompositeWhLocValue(value))
				return true;
			// Campo opcional com valor inválido (-2): tratar como vazio para permitir avançar (usuário digitou algo que não está na lista)
			if (!this.isRequired() && (value == -2 || value == '-2'))
				return true;
			return false;
		},

		isEmpty(value = this.getValue())
		{
			// Não usar !value: device_id 0 é válido; só null/undefined/''/-1 são vazios
			return value == null || value === '' || value == -1 || value === '-1';
		},

		refresh()
		{
			let node = document.querySelector(this.selector);
			if (!node || !node.list)
				return;
			let value = this.getValue();
			let option = findOption(node, value);
			if (option)
				node.value = option.innerText;
			else if (value != -2)
			{
				// Opções ainda não carregaram: manter id (ou composto wh:loc) como fallback em vez de vazio
				const s = String(value ?? '').trim();
				if (isCompositeWhLocValue(s))
					node.value = s;
				else
					node.value = isValidId(value) ? String(value) : '';
			}
		},

		handleEvents(ev)
		{
			switch (ev.type)
			{
				case 'input':														// Event generated when user select an option from the dropdown list
					if (!ev.inputType || ev.inputType == 'insertReplacementText')	// React to list selections only
					{
						const options = Array.from(ev.target?.list?.options ?? []);
						const raw = String(ev.target.value).trim();
						const prev = this.getValue();
						const byValue = options.filter(o => String(o.value) === raw || o.value == raw);
						let candidates = byValue.length ? byValue : options.filter(o => o.innerText && o.innerText.trim() === raw);
						if (!candidates.length)
							candidates = options.filter(o => optionMatchesTyped(o, raw));
						const option = disambiguateByPreferred(candidates, prev);
						setOption.call(this, ev.target, (o) => o === option);
					}
					break;
				case 'blur':
				{
					let node = document.querySelector(this.selector);
					if (node.value != findOption(node, this.getValue())?.innerText)
					{
						let options = Array.from(ev.target?.list?.options ?? []);
						let resolved = resolveOptionOnBlur(options, ev.target.value, this.getValue());
						setOption.call(this, ev.target, (option) => option === resolved);
					}
					break;
				}
			}
		}

	};
}

